import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function OwnerCustomOffers() {
  const { user } = useAuth();
  const { t } = useI18n();

  const [tierInfo, setTierInfo] = useState(null);
  const [offers, setOffers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    title: '',
    title_th: '',
    description: '',
    description_th: '',
    image_url: '',
    category_id: '',
    contact_phone: '',
    contact_website: '',
    terms: '',
    terms_th: '',
    active: true
  });

  // Property assignment state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningOfferId, setAssigningOfferId] = useState(null);
  const [assignedProperties, setAssignedProperties] = useState([]);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  async function loadData() {
    try {
      const [tierData, offersData, categoriesData, propertiesData] = await Promise.all([
        api.get('/owner/tier-info'),
        api.get('/owner/custom-offers').catch(() => []),
        api.get('/guest/categories'),
        api.get('/owner/properties')
      ]);
      setTierInfo(tierData);
      setOffers(offersData);
      setCategories(categoriesData);
      setProperties(propertiesData);
    } catch (err) {
      console.error('Failed to load:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/owner/custom-offers/\${editingId}`, form);
      } else {
        await api.post('/owner/custom-offers', form);
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this custom offer?')) return;
    try {
      await api.delete(`/owner/custom-offers/\${id}`);
      loadData();
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  function handleEdit(offer) {
    setForm({
      title: offer.title || '',
      title_th: offer.title_th || '',
      description: offer.description || '',
      description_th: offer.description_th || '',
      image_url: offer.image_url || '',
      category_id: offer.category_id || '',
      contact_phone: offer.contact_phone || '',
      contact_website: offer.contact_website || '',
      terms: offer.terms || '',
      terms_th: offer.terms_th || '',
      active: offer.active
    });
    setEditingId(offer.id);
    setShowForm(true);
  }

  function resetForm() {
    setForm({
      title: '',
      title_th: '',
      description: '',
      description_th: '',
      image_url: '',
      category_id: '',
      contact_phone: '',
      contact_website: '',
      terms: '',
      terms_th: '',
      active: true
    });
  }

  async function handleOpenAssign(offer) {
    setAssigningOfferId(offer.id);
    try {
      // Get current assignments for all properties
      const assignments = await Promise.all(
        properties.map(async (p) => {
          const linked = await api.get(`/owner/properties/\${p.id}/custom-offers`).catch(() => []);
          return {
            propertyId: p.id,
            isAssigned: linked.some(l => l.id === offer.id)
          };
        })
      );
      setAssignedProperties(assignments);
      setShowAssignModal(true);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    }
  }

  async function handleTogglePropertyAssignment(propertyId, currentlyAssigned) {
    try {
      if (currentlyAssigned) {
        await api.delete(`/owner/properties/\${propertyId}/custom-offers/\${assigningOfferId}`);
      } else {
        await api.post(`/owner/properties/\${propertyId}/custom-offers`, {
          owner_offer_id: assigningOfferId
        });
      }
      // Update local state
      setAssignedProperties(prev => prev.map(a => 
        a.propertyId === propertyId ? { ...a, isAssigned: !currentlyAssigned } : a
      ));
      loadData(); // Refresh offer counts
    } catch (err) {
      alert('Error: ' + err.message);
    }
  }

  if (loading) {
    return <div className="flex justify-center" style={{ padding: '40px 0' }}><div className="spinner"></div></div>;
  }

  // Check if owner has required tier
  if (tierInfo && tierInfo.tier === 'basic') {
    return (
      <div className="card text-center" style={{ padding: '40px' }}>
        <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>Custom Offers</h2>
        <p className="text-secondary mb-4">
          Custom offers require a Middle or Premium tier subscription.
        </p>
        <p className="text-secondary">
          Your current tier: <strong style={{ color: '#6c757d' }}>Basic</strong>
        </p>
        <p className="text-secondary mt-4" style={{ fontSize: '12px' }}>
          Contact support to upgrade your tier.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: '600' }}>Custom Offers</h1>
          <p className="text-secondary text-xs mt-1">
            Create and manage your own offers for your properties
          </p>
        </div>
        <button 
          onClick={() => { setShowForm(true); setEditingId(null); resetForm(); }} 
          className="btn btn-primary" 
          style={{ padding: '8px 16px', fontSize: '14px' }}
        >
          + New Offer
        </button>
      </div>

      {/* Tier Badge */}
      {tierInfo && (
        <div className="card mb-4" style={{ padding: '12px 16px', background: tierInfo.tier === 'premium' ? '#ffc10720' : '#0d6efd20' }}>
          <div className="flex items-center gap-2">
            <span 
              style={{ 
                background: tierInfo.tier === 'premium' ? '#ffc107' : '#0d6efd',
                color: tierInfo.tier === 'premium' ? '#000' : 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600'
              }}
            >
              {tierInfo.tier.toUpperCase()} TIER
            </span>
            <span className="text-secondary text-xs">
              {tierInfo.features?.description || ''}
            </span>
          </div>
        </div>
      )}

      {/* Create/Edit Form */}
      {showForm && (
        <div className="card mb-4">
          <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>
            {editingId ? 'Edit Custom Offer' : 'New Custom Offer'}
          </h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="label">Title (English) *</label>
              <input 
                type="text" 
                className="input" 
                value={form.title} 
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} 
                required 
              />
            </div>
            <div className="form-group">
              <label className="label">Title (Thai)</label>
              <input 
                type="text" 
                className="input" 
                value={form.title_th} 
                onChange={(e) => setForm(f => ({ ...f, title_th: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label className="label">Description (English)</label>
              <textarea 
                className="input" 
                rows={3}
                value={form.description} 
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label className="label">Description (Thai)</label>
              <textarea 
                className="input" 
                rows={3}
                value={form.description_th} 
                onChange={(e) => setForm(f => ({ ...f, description_th: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label className="label">Category</label>
              <select 
                className="input" 
                value={form.category_id} 
                onChange={(e) => setForm(f => ({ ...f, category_id: e.target.value }))}
              >
                <option value="">-- Select Category --</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="label">Image URL</label>
              <input 
                type="url" 
                className="input" 
                value={form.image_url} 
                onChange={(e) => setForm(f => ({ ...f, image_url: e.target.value }))} 
                placeholder="https://..."
              />
            </div>
            <div className="flex gap-4">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Contact Phone</label>
                <input 
                  type="text" 
                  className="input" 
                  value={form.contact_phone} 
                  onChange={(e) => setForm(f => ({ ...f, contact_phone: e.target.value }))} 
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="label">Contact Website</label>
                <input 
                  type="url" 
                  className="input" 
                  value={form.contact_website} 
                  onChange={(e) => setForm(f => ({ ...f, contact_website: e.target.value }))} 
                />
              </div>
            </div>
            <div className="form-group">
              <label className="label">Terms & Conditions</label>
              <textarea 
                className="input" 
                rows={2}
                value={form.terms} 
                onChange={(e) => setForm(f => ({ ...f, terms: e.target.value }))} 
              />
            </div>
            <div className="form-group">
              <label>
                <input 
                  type="checkbox" 
                  checked={form.active} 
                  onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))} 
                /> Active
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="btn btn-primary">{t('common.save')}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {/* Offers List */}
      {offers.length === 0 ? (
        <div className="card text-center text-secondary" style={{ padding: '40px' }}>
          <p>No custom offers yet.</p>
          <p className="text-xs mt-2">Create your first custom offer to show on your properties.</p>
        </div>
      ) : (
        offers.map(offer => (
          <div key={offer.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <div className="flex items-center gap-2">
                <h3 style={{ fontSize: '14px' }}>{offer.title}</h3>
                {!offer.active && (
                  <span className="badge" style={{ background: 'var(--error)', color: 'white', fontSize: '10px', padding: '2px 6px' }}>
                    Inactive
                  </span>
                )}
              </div>
              <p className="text-secondary text-xs">
                {offer.category_name || 'No category'} • {offer.property_count || 0} properties
              </p>
              {offer.description && (
                <p className="text-secondary text-xs mt-1" style={{ maxWidth: '400px' }}>
                  {offer.description.substring(0, 100)}{offer.description.length > 100 ? '...' : ''}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button 
                onClick={() => handleOpenAssign(offer)} 
                className="btn btn-secondary" 
                style={{ padding: '6px 10px', fontSize: '12px' }}
              >
                Assign
              </button>
              <button 
                onClick={() => handleEdit(offer)} 
                className="btn btn-secondary" 
                style={{ padding: '6px 10px', fontSize: '12px' }}
              >
                {t('common.edit')}
              </button>
              <button 
                onClick={() => handleDelete(offer.id)} 
                className="btn btn-secondary" 
                style={{ padding: '6px 10px', fontSize: '12px', color: 'var(--error)' }}
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))
      )}

      {/* Property Assignment Modal */}
      {showAssignModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowAssignModal(false)}
        >
          <div 
            className="card" 
            style={{ maxWidth: '400px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontSize: '16px', marginBottom: '16px' }}>Assign to Properties</h3>
            {properties.length === 0 ? (
              <p className="text-secondary">No properties found.</p>
            ) : (
              properties.map(prop => {
                const assignment = assignedProperties.find(a => a.propertyId === prop.id);
                const isAssigned = assignment?.isAssigned || false;
                return (
                  <div 
                    key={prop.id} 
                    className="flex items-center justify-between" 
                    style={{ padding: '8px 0', borderBottom: '1px solid var(--border)' }}
                  >
                    <div>
                      <p style={{ fontSize: '14px' }}>{prop.name}</p>
                      <p className="text-secondary text-xs">{prop.unit_code}</p>
                    </div>
                    <button
                      onClick={() => handleTogglePropertyAssignment(prop.id, isAssigned)}
                      className={isAssigned ? 'btn btn-primary' : 'btn btn-secondary'}
                      style={{ padding: '4px 12px', fontSize: '12px' }}
                    >
                      {isAssigned ? 'Assigned ✓' : 'Assign'}
                    </button>
                  </div>
                );
              })
            )}
            <div className="mt-4">
              <button 
                onClick={() => setShowAssignModal(false)} 
                className="btn btn-secondary" 
                style={{ width: '100%' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OwnerCustomOffers;
